<script>
import { computed, defineComponent } from 'vue'
const rootCarouselContext = require.context('@/assets/carousal', false, /album-\d+\.png$/)
const seasonalContexts = {
  rap: require.context('@/assets/carousal/seasonalCarousals/rap', false, /rap-album-\d+\.png$/),
  pop: require.context('@/assets/carousal/seasonalCarousals/pop', false, /pop-album-\d+\.png$/),
  rock: require.context('@/assets/carousal/seasonalCarousals/rock', false, /rock-album-\d+\.png$/),
}

export default defineComponent({
  name: 'CarouselComponent',
  props: {
    season: {
      type: String,
      required: false,
      default: ''
    },
    speed: {
      type: Number,
      required: false,
      default: 45
    },
    itemHeight: {
      type: Number,
      required: false,
      default: 100
    },
    gap: {
      type: Number,
      required: false,
      default: 10
    }
  },
  setup(props) {
    const normalizeSeason = (value) => {
      if (!value) return ''
      const v = String(value).toLowerCase().trim()
      if (['rap', 'pop', 'rock', 'all'].includes(v)) return v
      // Map seasonal themes to their carousel genre
      const seasonToGenre = {
        'summer': 'pop',
        'back2school': 'pop',
        'vday': 'pop',
        'autumn': 'rap',
        'easter': 'rap',
        'winter': 'rock',
        'christmas': 'rock',
      }
      return seasonToGenre[v] || ''
    }

    const images = computed(() => {
      const selectedSeason = normalizeSeason(props.season)

      if (selectedSeason === 'all') {
        const merged = ['rap', 'pop', 'rock']
            .map((k) => seasonalContexts[k])
            .filter(Boolean)
            .flatMap((ctx) => ctx.keys().map((key) => ({ key, url: ctx(key) })))

        const extractNumAll = (k) => {
          const match = k.match(/(\d+)(?=\.[a-z]+$)/i)
          return match ? parseInt(match[1], 10) : 0
        }
        merged.sort((a, b) => extractNumAll(a.key) - extractNumAll(b.key))
        return merged.map(e => e.url)
      }

      const context = selectedSeason ? seasonalContexts[selectedSeason] : rootCarouselContext
      if (!context) return []

      const entries = context.keys().map((key) => ({ key, url: context(key) }))

      const extractNum = (k) => {
        const match = k.match(/(\d+)(?=\.[a-z]+$)/i)
        return match ? parseInt(match[1], 10) : 0
      }
      entries.sort((a, b) => extractNum(a.key) - extractNum(b.key))

      return entries.map(e => e.url)
    })

    const animationStyle = computed(() => ({
      animationDuration: `${Math.max(8, props.speed)}s`,
      gap: `${props.gap}px`
    }))

    const itemStyle = computed(() => ({
      height: `${props.itemHeight}px`
    }))

    return { images, animationStyle, itemStyle }
  }
})
</script>

<template>
  <div class="d-flex flex-column justify-content-center align-items-center col-12 carousel-wrapper">
    <div class="d-flex justify-content-center align-items-center col-12 marquee" role="region" aria-label="album carousel">
      <div class="track" :style="animationStyle">
        <!-- First set of images -->
        <div class="item" v-for="(src, idx) in images" :key="`a-${idx}`" :style="itemStyle">
          <img class="img" :src="src" alt="album" decoding="async" loading="lazy" />
        </div>
        <!-- Duplicate set for seamless loop -->
        <div class="item" v-for="(src, idx) in images" :key="`b-${idx}`" :style="itemStyle">
          <img class="img" :src="src" alt="album duplicate" decoding="async" loading="lazy" />
        </div>
        <!-- Third set for extra smoothness -->
        <div class="item" v-for="(src, idx) in images" :key="`c-${idx}`" :style="itemStyle">
          <img class="img" :src="src" alt="album duplicate" decoding="async" loading="lazy" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.carousel-wrapper {
  padding: 10px 0;
}

.marquee {
  overflow: hidden;
  position: relative;
}

.track {
  display: flex;
  align-items: center;
  will-change: transform;
  animation-name: scroll-left;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.item {
  flex: 0 0 auto;
  border: 1px solid #FFF;
  background-color: #fff;
  box-shadow: 1px 1px 1px 1px rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.img {
  height: 100%;
  width: auto;
  display: block;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-33.333%);
  }
}
</style>